import api from '@/services/base/BaseApi';
import { handleApiError } from '@/services/base/handleApiError';
import { TMultiSignAction, MultiSignActionStateEnum } from '@/services/types/MultiSign';
import {
  ScheduledBankPaymentGroupStateEnum,
  ScheduledPaymentIntervalTypeEnum,
  TScheduledPaymentIntervalType
} from '@/features/payments/types/BatchPayment';
import {
  PaymentRowKindEnum,
  TPaymentListItem
} from '@/features/payments/manage/types/ManagePayments';

export const authoriseMultiSignAction = (actionId: string) =>
  api
    .post<TMultiSignAction, null>(`api/v1/multi-sign/actions/${actionId}/authorise`, null)
    .catch(handleApiError);

export const getMultiSignActionById = (actionId: string) =>
  api.get<TMultiSignAction>(`api/v1/multi-sign/actions/${actionId}`).catch(handleApiError);

// Mirrors RqRejectMultiSignAction: `notes`, required, max 200 chars. The reject dialog collects
// this under the "Reason" label but sends it on the wire as `notes` to match the backend field.
export const rejectMultiSignAction = (actionId: string, notes: string) =>
  api
    .post<unknown, { notes: string }>(`api/v1/multi-sign/actions/${actionId}/reject`, { notes })
    .catch(handleApiError);

// Action types with no EScheduledBankPayment row yet (creation is deferred until the multi-sign
// action completes), so the action itself is the only source for a payment still awaiting
// approval. Includes `send_bank_payment` - an immediate (one-off, non-scheduled) payment is
// still implemented backend-side as its own action type distinct from
// `create_scheduled_bank_payment`, but both need to surface here as pending rows.
const PENDING_PAYMENT_ACTION_TYPES = ['create_scheduled_bank_payment', 'send_bank_payment'];

// Mirrors RqCreateScheduledBankPayment - the JSON serialized into RsMultiSignAction.data for a
// pending create_scheduled_bank_payment action.
interface IParsedCreateScheduledBankPaymentData {
  walletId: string;
  paymentDate: string;
  amount: number;
  payeeBankAccountNumber: string;
  payeeName: string;
  recipientName?: string | null;
  payeeParticulars?: string | null;
  payeeAnalysis?: string | null;
  payeeReference?: string | null;
  payerParticulars?: string | null;
  payerAnalysis?: string | null;
  payerReference?: string | null;
  paymentIntervalType: TScheduledPaymentIntervalType | null;
  paymentIntervalValue: number | null;
}

const transformPendingScheduledPaymentAction = (action: TMultiSignAction): TPaymentListItem => {
  const data: IParsedCreateScheduledBankPaymentData = JSON.parse(action.data);

  return {
    id: action.id,
    name: data.recipientName ?? data.payeeName,
    kind: PaymentRowKindEnum.PENDING_SCHEDULED_PAYMENT,
    paymentIntervalType: data.paymentIntervalType ?? ScheduledPaymentIntervalTypeEnum.ONE_OFF,
    // Needed alongside paymentIntervalType for the Scheduled date column's recurrence annotation
    // (see buildRecurringDateText) - e.g. distinguishing "every week" from "every 3 weeks".
    paymentIntervalValue: data.paymentIntervalValue ?? undefined,
    createdDate: action.created,
    scheduledDate: data.paymentDate,
    totalAmount: data.amount,
    walletId: data.walletId,
    // Always PENDING_APPROVAL - the row exists at all only because it's still awaiting
    // multi-sign approval - so every state-keyed lookup (status label/style,
    // ACTION_BY_STATE -> APPROVAL_REQUIRED) works unchanged, same as for groups.
    state: ScheduledBankPaymentGroupStateEnum.PENDING_APPROVAL,
    totalPayments: 1,
    createdByCustomerId: action.triggeredByCustomerId,
    multiSignActionId: action.id,
    paymentDetail: {
      id: action.id,
      payeeName: data.payeeName,
      payeeBankAccount: data.payeeBankAccountNumber,
      payeeParticulars: data.payeeParticulars,
      payeeAnalysis: data.payeeAnalysis,
      payeeReference: data.payeeReference,
      payerParticulars: data.payerParticulars,
      payerAnalysis: data.payerAnalysis,
      payerReference: data.payerReference
    }
  };
};

/**
 * Individual scheduled payments still awaiting multi-sign approval - sourced from the multi-sign
 * action itself (see transformPendingScheduledPaymentAction) rather than /scheduled-payments,
 * since no EScheduledBankPayment row exists until the action completes.
 *
 * Callers pass both accountId and businessId unconditionally (whatever useAuth currently has) -
 * businessId takes priority when present since accountId only applies to a personal account; the
 * caller doesn't need to know which one is relevant for the current viewer.
 */
export const getPendingScheduledPaymentActions = async (
  accountId?: string,
  businessId?: string
): Promise<TPaymentListItem[]> => {
  const params = new URLSearchParams();
  if (businessId) {
    params.append('businessId', businessId);
  } else if (accountId) {
    params.append('accountId', accountId);
  }
  params.append('states', MultiSignActionStateEnum.PENDING);
  const responseBody = await api
    .get<TMultiSignAction[]>(`api/v1/multi-sign/actions?${params.toString()}`)
    .catch(handleApiError);

  return responseBody.data
    .filter((action) => PENDING_PAYMENT_ACTION_TYPES.includes(action.type))
    .map(transformPendingScheduledPaymentAction);
};
